import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, combineLatest, of } from 'rxjs';
import { takeUntil, switchMap, map, filter, catchError } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { UserService } from '../../auth/user.service';
import { ModelService } from '../model.service';
import { MediaService } from '../../media/media.service';
import { Model } from '../../models/model';
import { User } from '../../models/user';

interface ChecklistItem {
  id: string;
  label: string;
  isComplete: boolean;
  isOptional: boolean;
  routerLink?: string;
}

@Component({
  selector: 'app-model-onboarding-checklist',
  templateUrl: './model-onboarding-checklist.component.html',
  styleUrls: ['./model-onboarding-checklist.component.css']
})
export class ModelOnboardingChecklistComponent implements OnInit, OnDestroy {
  /**
   * Optional: Pass the model ID explicitly (useful when viewing own profile).
   * If not provided, defaults to current user's UID.
   */
  @Input() modelId?: string;

  private destroy$ = new Subject<void>();

  /** Whether the component should be visible */
  isVisible = false;

  /** Whether loading data */
  isLoading = true;

  /** Calculated progress percentage (0-100) */
  progressPercentage = 0;

  /** List of checklist items */
  checklistItems: ChecklistItem[] = [];

  /** Whether the profile is complete (>= 80%) */
  isProfileComplete = false;

  /** Whether to show the success message instead of checklist */
  showSuccessMessage = false;

  /** Threshold percentage for hiding checklist */
  private readonly COMPLETION_THRESHOLD = 80;

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private modelService: ModelService,
    private mediaService: MediaService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadChecklistData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loads user and model data to compute checklist state.
   * Uses RxJS combineLatest/switchMap pattern - no nested subscriptions.
   */
  private loadChecklistData(): void {
    const uid = this.authService.getCurrentUid();
    
    if (!uid) {
      this.isVisible = false;
      this.isLoading = false;
      return;
    }

    // Get user document to check if they are a model with a profile
    this.userService.getUserById(uid).pipe(
      takeUntil(this.destroy$),
      switchMap((user: User | null) => {
        // Visibility rule: user must be authenticated, is_model=true, has_profile=true
        if (!user || !user.is_model || !user.has_profile) {
          return of(null);
        }

        // If model onboarding is already complete, don't show checklist
        if (user.isModelOnboardingComplete()) {
          return of(null);
        }

        // Determine which model ID to fetch
        const targetModelId = this.modelId || uid;

        // Only show checklist if viewing own profile
        if (targetModelId !== uid) {
          return of(null);
        }

        // Fetch model document AND actual media counts in parallel
        return combineLatest([
          this.modelService.getModelById(targetModelId).pipe(catchError(() => of(null))),
          this.mediaService.getUserMediaCounts(targetModelId).pipe(catchError(() => of({ imagesCount: 0, videosCount: 0 })))
        ]).pipe(
          map(([model, mediaCounts]) => {
            if (!model) return null;
            return { user, model, mediaCounts };
          })
        );
      })
    ).subscribe({
      next: (result) => {
        this.isLoading = false;

        if (!result || !result.model) {
          this.isVisible = false;
          return;
        }

        this.computeChecklistState(result.model, result.mediaCounts);
      },
      error: () => {
        this.isLoading = false;
        this.isVisible = false;
      }
    });
  }

  /**
   * Computes checklist items and progress percentage from model data.
   * @param model The model document
   * @param mediaCounts Actual media counts fetched from images/videos collections
   */
  private computeChecklistState(model: Model, mediaCounts: { imagesCount: number; videosCount: number }): void {
    const modelEditRoute = `/models/${model.model_id}/edit`;
    const contentRoute = '/content-manager';

    // Use actual media counts from MediaService (not stored counts on model document)
    const actualImagesCount = mediaCounts.imagesCount;
    const actualVideosCount = mediaCounts.videosCount;

    // Define checklist items with completion rules
    this.checklistItems = [
      {
        id: 'profile_picture',
        label: 'Add a profile picture',
        isComplete: !!model.profile_image_url && model.profile_image_url.length > 0,
        isOptional: false,
        routerLink: modelEditRoute
      },
      {
        id: 'bio',
        label: 'Write a bio',
        isComplete: !!model.description && model.description.trim().length > 0,
        isOptional: false,
        routerLink: modelEditRoute
      },
      {
        id: 'images',
        label: 'Upload at least 3 images',
        isComplete: actualImagesCount >= 3,
        isOptional: false,
        routerLink: contentRoute
      },
      {
        id: 'video',
        label: 'Upload a video',
        isComplete: actualVideosCount >= 1,
        isOptional: true,
        routerLink: contentRoute
      },
      {
        id: 'location',
        label: 'Add your location',
        isComplete: this.hasLocation(model),
        isOptional: false,
        routerLink: modelEditRoute
      }
    ];

    // Calculate progress
    this.progressPercentage = this.calculateProgress();

    // Determine visibility based on completion threshold
    this.isProfileComplete = this.progressPercentage >= this.COMPLETION_THRESHOLD;
    
    if (this.isProfileComplete) {
      // Hide checklist when profile is complete
      // The model-detail page handles showing the success banner separately
      this.isVisible = false;
      this.showSuccessMessage = false;
    } else {
      this.showSuccessMessage = false;
      this.isVisible = true;
    }
  }

  /**
   * Checks if the model has location information.
   */
  private hasLocation(model: Model): boolean {
    return (!!model.city && model.city.trim().length > 0) ||
           (!!model.province && model.province.trim().length > 0) ||
           (!!model.country && model.country.trim().length > 0);
  }

  /**
   * Calculates progress percentage.
   * Required items: 4 (profile picture, bio, images, location)
   * Optional items: 1 (video) - does not penalize if incomplete
   * 
   * Formula: ((completedRequired / totalRequired) * 90) + (completedOptional * 10)
   * This ensures optional items contribute but don't heavily penalize.
   */
  private calculateProgress(): number {
    const requiredItems = this.checklistItems.filter(item => !item.isOptional);
    const optionalItems = this.checklistItems.filter(item => item.isOptional);

    const completedRequired = requiredItems.filter(item => item.isComplete).length;
    const completedOptional = optionalItems.filter(item => item.isComplete).length;

    const totalRequired = requiredItems.length;
    const totalOptional = optionalItems.length;

    // Required items contribute 90% of the progress
    const requiredProgress = totalRequired > 0 
      ? (completedRequired / totalRequired) * 90 
      : 90;

    // Optional items contribute up to 10%
    const optionalProgress = totalOptional > 0
      ? (completedOptional / totalOptional) * 10
      : 0;

    return Math.round(requiredProgress + optionalProgress);
  }

  /**
   * Returns the icon for a checklist item based on completion status.
   */
  getItemIcon(item: ChecklistItem): string {
    return item.isComplete ? 'check_circle' : 'radio_button_unchecked';
  }

  /**
   * Returns the icon color class for a checklist item.
   */
  getItemIconClass(item: ChecklistItem): string {
    return item.isComplete ? 'complete-icon' : 'incomplete-icon';
  }

  /**
   * Navigates to the edit profile or content page.
   */
  navigateToImprove(item: ChecklistItem): void {
    if (item.routerLink) {
      this.router.navigate([item.routerLink]);
    }
  }

  /**
   * Dismisses the success message after profile completion.
   */
  dismissSuccessMessage(): void {
    this.isVisible = false;
  }

  /**
   * Refreshes the checklist data (useful after model updates).
   */
  refreshChecklist(): void {
    this.isLoading = true;
    this.loadChecklistData();
  }
}
